import moment from "moment";
import defaultTestRunColumnVisible from "../features/userPreference/DefaultFields/testrun.json";
import defaultTestCaseColumnVisible from "../features/userPreference/DefaultFields/testcase.json";
import defaultTestCaseDetailColumnVisible from "../features/userPreference/DefaultFields/testcasedetail.json";
import defaultProjectListColumnVisible from "../features/userPreference/DefaultFields/projectlist.json";
import defaultGenrateAITestCaseColumnVisible from "../features/userPreference/DefaultFields/genrateAITestCase.json";
import defaultApprovedAITestCaseColumnVisible from "../features/userPreference/DefaultFields/approvedAITestCase.json";
import { MissingData } from "../enums/MissingDataEnum";
import PermissionEnum from "../enums/PermissionEnum";
import { Box, Tooltip, Typography } from "@mui/material";
import { Link } from "react-router-dom";
export const DO_NOTHING = ()=>{};

export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

export const viewMap = {
  'testRun': 'TestRunPageConfigure',
  'testCase': 'TestCasePageConfigure',
  'testCaseDetail': 'TestCaseDetailPageConfigure',
  'projectList': 'ProjectListPageConfigure',
  'genrateAITestCase': 'GenrateAITestCasePageConfigure',
  'approvedAITestCase': 'ApprovedAITestCasePageConfigure'
}

export const viewColumnMap = {
  'testRun': defaultTestRunColumnVisible,
  'testCase': defaultTestCaseColumnVisible,
  'testCaseDetail': defaultTestCaseDetailColumnVisible,
  'projectList': defaultProjectListColumnVisible,
  'genrateAITestCase': defaultGenrateAITestCaseColumnVisible,
  'approvedAITestCase': defaultApprovedAITestCaseColumnVisible
}


export const extractUniqueIdsFromPaths = (hierarchies) => {
  try {
    if (!Array.isArray(hierarchies)) {
      throw new TypeError("Expected an array of hierarchies.");
    }

    const idSet = new Set(
      hierarchies
        .filter(
          (hierarchy) =>
            typeof hierarchy === "string" && hierarchy.trim() !== ""
        )
        .flatMap((hierarchy) => hierarchy.split(">").map((id) => id.trim()))
        .filter((id) => id.length > 0)
    );

    return Array.from(idSet);
  } catch (error) {
    console.error("Error extracting unique IDs:", error.message);
    return [];
  }
};

export function buildModuleTreeData(moduleData, moduleHierarchies) {
  if (!Array.isArray(moduleData)) {
    throw new Error("Invalid module data: Expected an array.");
  }
  if (!Array.isArray(moduleHierarchies)) {
    throw new Error("Invalid module hierarchies: Expected an array.");
  }

  const idToNameMap = Object.fromEntries(
    moduleData.map(({ id, name }) => {
      if (!id || typeof id !== "string") {
        throw new Error(`Invalid module id: ${id}`);
      }
      return [id, name || "Unnamed Module"];
    })
  );

  const tree = [];

  for (const hierarchyObj of moduleHierarchies) {
    const path = hierarchyObj?.path;

    if (typeof path !== "string") {
      continue;
    }

    const ids = path
      .split(/[<>]/)
      .map((id) => id.trim())
      .filter(Boolean);

    if (ids.length === 0) continue;

    let currentLevel = tree;
    let parentId = null;
    let level = 1;

    for (const id of ids) {
      let node = currentLevel.find((node) => node.id === id);

      if (!node) {
        node = {
          id,
          name: idToNameMap[id] || "Unknown Module",
          parent_id: parentId,
          level: level,
          children: [],
        };
        currentLevel.push(node);
      }

      parentId = id;
      currentLevel = node.children;
      level += 1;
    }
  }

  return tree;
}

export function mapLastNodeIdToNamePath(pathData, idNameMap) {
  try {
    const idToName = Object.fromEntries(idNameMap.map(({ id, name }) => [id, name]));
    const lastNodeMap = {};
  
    for (const item of pathData) {
      if (!item?.path || typeof item.path !== 'string') continue;
  
      const pathIds = item.path.split(" > ");
      const lastId = pathIds[pathIds.length - 1];
      const pathNames = pathIds.map(id => idToName[id] || id);
      const fullNamePath = pathNames.join(" / ");
  
      lastNodeMap[lastId] = fullNamePath; // store directly as string
    }
  
    return lastNodeMap;
  } catch (error) {
    console.error("Error mapping last node to name path:", error.message);
    return {};
  }
}


export function formatDate(data = []) {
  return data.map((item) => {
    const {
      startDate = "",
      endDate = "",
      summaryOfTestCaseStatus = {},
    } = item;
    const total =
      (summaryOfTestCaseStatus.Pending || 0) +
      (summaryOfTestCaseStatus.Failed || 0) +
      (summaryOfTestCaseStatus.Passed || 0);

    const summaryOfTestCaseStatusPercentages = {
      Pending: total
        ? ((summaryOfTestCaseStatus.Pending || 0) / total) * 100
        : 0,
      Failed: total
        ? ((summaryOfTestCaseStatus.Failed || 0) / total) * 100
        : 0,
      Passed: total
        ? ((summaryOfTestCaseStatus.Passed || 0) / total) * 100
        : 0,
    };
    return {
      ...item,
      formattedStartDate: moment(startDate).format("MMM DD, YYYY"),
      formattedEndDate: moment(endDate).format("MMM DD, YYYY"),
      testCaseStatusPercentages: summaryOfTestCaseStatusPercentages,
    };
  });
}

export const findNodeById = (nodes, id) => {
  for (const node of nodes) {
      if (node.id === id) {
          return node;
      }
      if (node.children?.length) {
          const found = findNodeById(node.children, id);
          if (found) return found;
      }
  }
  return null;
};

export function formatNumber(num) {
  return Number.isInteger(num) ? num : parseFloat(num.toFixed(2));
}

export const stripHtml = (input) => {
  if (!input || typeof input !== 'string') return '';

  const doc = new DOMParser().parseFromString(input, 'text/html');
  return doc.body.textContent?.trim() || '';
};

export const formatDuration = (duration, full=false) => {
  if (duration === null || duration === undefined) {
    return duration;
  }

  let tDuration = Number(duration);

  if (isNaN(tDuration) || !isFinite(tDuration)) {
    return "Invalid duration";
  }

  if (tDuration < 0) {
    return "";
  }

  if (tDuration < 1000) {
    return `${tDuration} ${full ? "milliseconds" :  "ms"}`;
  } else if (tDuration < 60000) {
    return `${(tDuration / 1000).toFixed(2)} ${full ? "seconds" :  "s"}`;
  } else {
    return `${(tDuration / 60000).toFixed(2)} ${full ? "minutes" :  "m"}`;
  }
};

export function formatDurationInSeconds(seconds) {
  seconds = seconds/1000;
  if (isNaN(seconds) || seconds < 1) return '0s';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  let str = '';
  if (h > 0) str += `${h}h `;
  if (m > 0) str += `${m}m `;
  if (s > 0 || (!h && !m)) str += `${s}s`;
  return str.trim();
}

export const safeParseJson = (jsonString) => {
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return null;
  }
}

export const getIconCoverClassName = (buttonName) => {
  switch (buttonName) {
    case 'Delete':
      return 'delete-icon';
    case 'Move':
      return 'move-icon';
    case 'Clone':
      return 'clone-icon';
    case 'Add':
    case '+ Add Test Case':
      return 'add-icon';
    case 'not-contained':
      return 'not-contained-icon';
    case 'Add to Test Group':
      return 'add-testcase-icon';
    case 'disabled':
      return 'disabled-icon';
    default:
      return 'default-icon';
  }
}

export const renderTableCell = (value, classList = '', props = {}) => {
  
  const normalizedValue = (typeof value === 'string' ? value.trim() : value);

  const displayValue = normalizedValue || MissingData.NONE;
  const tooltipValue = normalizedValue || MissingData.NO_DATA;

  return (
    <Tooltip title={tooltipValue}> 
      {props.type === 'link' ? ( 
        <span className={`table-cell-container`} >
          <Link 
            to={props.to || '#'} 
            className={`link`} 
            onClick={(e) => {
              if (props?.onClick) {
                props.onClick(e); 
              }
            }}
          >
            <span className="ellipsis-container-line-2" style={{whiteSpace: 'pre'}}>{displayValue}</span>
          </Link>
        </span>
      ) : (
        <div className={`table-cell-container ${classList}`}>
          {props.children 
            ? props.children 
            : <span className="ellipsis-container-line-2" style={{whiteSpace: 'pre'}}>{displayValue}</span>}
        </div>
      )}
    </Tooltip>
  );
};

export function scrollChildIntoView({parentId, childId, delay=100}) {
  setTimeout(()=>{
    const parent = document.getElementById(parentId);
    const child = document.getElementById(childId);
  
    if (!parent || !child) return;
  
    const childTop = child.offsetTop - parent.offsetTop;
    parent.scrollTo({
      top: childTop - parent.clientHeight / 2 + child.clientHeight / 2,
      behavior: "smooth"
    });

    if (typeof child.focus === "function") {
      child.focus();
    }
  }, delay)
}

export function randomAlphaNumeric(length = 56) {
  return Array.from({ length }, () =>
    Math.random().toString(36)[2]
  ).join('');
}

/** LLM Configuration UI: show Section Model list when at least one provider is connected. */
export function shouldShowSectionModelList(providers) {
  return Array.isArray(providers) && providers.some((p) => p && p.connected === true);
}

/**
 * Same rule as Sidebar → Configuration visibility: `user.permissions` includes `get_ai_model` (Manage AI Configuration).
 * Also merges project `consolidatedPermissions[projectId]` when `projectId` is set (project-scoped roles).
 */
export function userCanSelectAiModel(user, projectId) {
  if (!user) return false;
  const permName = PermissionEnum.GET_AI_MODEL.name;
  const userPermission = user.permissions;
  if (Array.isArray(userPermission) && userPermission.includes(permName)) return true;
  if (projectId == null || projectId === "") return false;
  const consolidated =
    user.project_roles_permissions?.consolidatedPermissions || {};
  const projectPerms =
    consolidated[projectId] ?? consolidated[String(projectId)];
  return Array.isArray(projectPerms) && projectPerms.includes(permName);
}

/**
 * useWebSocket.connect() may resolve to true, { success: true, usageData? }, { success: false, message? }, or false.
 */
export function isWebSocketConnectSuccess(result) {
  if (result === true) return true;
  if (result && typeof result === 'object' && result.success === true) return true;
  return false;
}

export function getWebSocketConnectErrorMessage(result, fallback) {
  if (
    result &&
    typeof result === 'object' &&
    result.success === false &&
    typeof result.message === 'string' &&
    result.message.trim()
  ) {
    return result.message;
  }
  return fallback;
}